import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "react-feather";

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  subtitle,
  actions,
}) => (
  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
    <div>
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      {subtitle ? (
        <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
      ) : null}
    </div>
    {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
  </div>
);

interface SectionCardProps {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  title,
  description,
  actions,
  children,
  collapsible = false,
  defaultOpen = true,
}) => (
  <CollapsibleSection
    title={title}
    description={description}
    actions={actions}
    collapsible={collapsible}
    defaultOpen={defaultOpen}
  >
    {children}
  </CollapsibleSection>
);

interface CollapsibleSectionProps extends SectionCardProps {}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  description,
  actions,
  children,
  collapsible = false,
  defaultOpen = true,
}) => {
  const initialOpen = useMemo(
    () => (collapsible ? defaultOpen : true),
    [collapsible, defaultOpen]
  );
  const [isOpen, setIsOpen] = useState(initialOpen);
  const sectionId = useMemo(
    () =>
      title
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9\-]/g, ""),
    [title]
  );

  useEffect(() => {
    if (!collapsible) {
      setIsOpen(true);
      return;
    }
    setIsOpen(defaultOpen);
  }, [collapsible, defaultOpen]);

  const toggle = () => {
    if (!collapsible) return;
    setIsOpen((prev) => !prev);
  };

  const showBody = !collapsible || isOpen;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div
        className={`flex items-center justify-between gap-3 px-6 py-5 transition ${
          collapsible
            ? "cursor-pointer bg-gradient-to-r from-white to-gray-50 hover:from-gray-50 hover:to-gray-100"
            : ""
        }`}
        onClick={toggle}
        role={collapsible ? "button" : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? isOpen : undefined}
        aria-controls={collapsible ? `${sectionId}-body` : undefined}
        onKeyDown={(event) => {
          if (!collapsible) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggle();
          }
        }}
      >
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          {description ? (
            <p className="text-sm text-gray-500">{description}</p>
          ) : null}
          {collapsible ? (
            <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
              <span className="h-1.5 w-1.5 rounded-full bg-gray-300"></span>
              {isOpen ? "Tap to collapse" : "Tap to expand"}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          {actions}
          {collapsible ? (
            <ChevronDown
              className={`h-5 w-5 text-gray-500 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          ) : null}
        </div>
      </div>
      {showBody ? (
        <div
          id={collapsible ? `${sectionId}-body` : undefined}
          className="px-6 pb-6 pt-0"
        >
          {children}
        </div>
      ) : null}
    </div>
  );
};

interface StatCardProps {
  label: string;
  value: string;
  trendLabel?: string;
  accent?: "primary" | "neutral" | "success" | "warning";
}

const ACCENT_BG: Record<string, string> = {
  primary: "bg-[var(--primary-color)]/10 text-[var(--primary-color)]",
  neutral: "bg-gray-100 text-gray-600",
  success: "bg-emerald-100 text-emerald-600",
  warning: "bg-amber-100 text-amber-600",
};

export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  trendLabel,
  accent = "neutral",
}) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
    <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">
      {label}
    </div>
    <div className="mt-3 text-3xl font-semibold text-gray-900">{value}</div>
    {trendLabel ? (
      <div
        className={`mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${ACCENT_BG[accent]}`}
      >
        {trendLabel}
      </div>
    ) : null}
  </div>
);
